import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export interface StepIndicatorStep {
  label: string;
}

interface StepView extends StepIndicatorStep {
  number: number;
  state: 'complete' | 'active' | 'pending';
}

/**
 * Shared progress indicator for multi-step flows (booking + reschedule).
 *
 * Promoted from the booking-feature-local `bodyanalytics-booking-step-indicator`
 * (APP-53 / F-FLOW-1) so the reschedule screens in `@bodyanalytics/calendar-ui`
 * can share it without crossing a feature→shared layer boundary.
 *
 * Visual states (per DESIGN.md + RescheduleFlow.jsx reference):
 *  - active   → amber circle, amber label
 *  - complete → primary circle + check, primary label, primary connector bar
 *  - pending  → muted circle/label
 *
 * Accessibility:
 *  - `role="list"` + `<li role="listitem">` per step
 *  - `aria-current="step"` on the active step
 *  - `aria-label` summarising "Step N of M: {label}"
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-step-indicator',
  standalone: true,
  templateUrl: './step-indicator.component.html',
  styleUrls: ['./step-indicator.component.scss'],
})
export class StepIndicatorComponent {
  readonly steps = input.required<StepIndicatorStep[]>();
  readonly currentStep = input.required<number>();

  protected readonly stepViews = computed<StepView[]>(() =>
    this.steps().map((s, i) => {
      const n = i + 1;
      const cur = this.currentStep();
      const state: StepView['state'] = n < cur ? 'complete' : n === cur ? 'active' : 'pending';
      return { ...s, number: n, state };
    }),
  );

  protected readonly ariaLabel = computed(() => {
    const total = this.steps().length;
    const cur = this.currentStep();
    const label = this.steps()[cur - 1]?.label ?? '';
    return `Step ${cur} of ${total}: ${label}`;
  });
}
